import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import {Observable} from 'rxjs';
import {
  IFirebaseLabelDesignsData,
  ILabelDesign,
  ILabelVariable,
  ILayout,
  IPreviewData,
  ITestLabelData,
} from '../../models/label-interfaces';
import * as JsBarcode from 'jsbarcode';
import {
  CollectionStoresSettingsService,
} from '../../../../../shared-services/firebase/collection-stores-settings.service';
import {path_stores_storeId_settings_shelf_talkers_settings} from '../../../../../shared-services/database-paths';
import {IStore} from '../../../../../shared-models/store/store';
import {Store} from '@ngrx/store';
import {CollectionGlobalService} from '../../../../../shared-services/firebase/collection-global.service';
import {IconColourEnum} from '../../../../../../shared-modules/shared-icons/models/icons-colour.enum';
import {ISharedModalBasic, ISharedModalBasicResponse} from '../../../../../shared-models/modals/shared-modal-basic';
import {SharedModalBasicComponent} from '../../../shared-modal-basic/shared-modal-basic.component';
import {CdkDragDrop} from '@angular/cdk/drag-drop';
import {isValidBase64} from '../../models/label-utils.utils';
import {setSelectedStore} from '../../../../../../features/user-access/user-stores-store/user-store.actions';
import {selectSelectedStore} from '../../../../../../features/user-access/user-stores-store/user-store.selectors';
import {IconsEnum} from '../../../../../../shared-modules/shared-icons/models/icons.enum';
import {IModalDismissResult, ModalControllerService} from '../../../../../shared-services/overlay/modal-controller.service';
import {createSharedPaths} from '../../../../../shared-services/database-paths.shared';
import {createGlobalPaths} from '../../../../../shared-services/database-paths.global';
import {
  ShelfTalkerLayoutClipboardService,
} from '../../services/shelf-talker-layout-clipboard.service';
import {getShelfTalkerSettings} from '../../../../../../features/shelf-talkers/store/shelf-talkers.actions';

@Component({
  selector: 'app-label-designer',
  templateUrl: './label-designer.component.html',
  styleUrls: ['./label-designer.component.scss'],
  standalone: false,
})
export class LabelDesignerComponent implements AfterViewInit, OnDestroy {

  //#region Variable Declarations
  @ViewChild('labelCanvas') canvasRef!: ElementRef<HTMLCanvasElement>;
  @ViewChild('rightSidenav') rightSidenav!: ElementRef<HTMLDivElement>;
  selectedStore: IStore;
  @Output() readonly printRequest = new EventEmitter<{ design: ILabelDesign, data: ITestLabelData[] }>();
  labelWidthMm = 100;
  labelHeightMm = 50;
  gridSizeMm = 1;
  previewScale = 1;
  zoomLevel = 1;
  zoomLevels = [0.5, 0.75, 1, 1.25, 1.5, 2, 3, 4];
  previewData: IPreviewData[] = [
    {
      description: 'Short description txt',
      barcode: '978020137962',
      itemCode: 'ITEM-001',
      price: '100.00',
      binLoc: 'A1-B2',
      date: new Date().toLocaleDateString('en-GB',
        {day: '2-digit', month: '2-digit', year: 'numeric'}),
      logo: 'COMPANY NAME',
      suppCode: '1234567890qwerty',
      logoUrl: '/assets/logos/Build-It-Logo-RED.png',
      companyName: 'COMPANY NAME',
    },
    {
      description: 'Medium description text exactly 30ch',
      barcode: '978316148410',
      itemCode: 'ITEM-002',
      price: '1,000.00',
      binLoc: 'C3-D4',
      date: new Date().toLocaleDateString('en-GB',
        {day: '2-digit', month: '2-digit', year: 'numeric'}),
      logo: 'COMPANY NAME',
      suppCode: '1234567890qwerty',
      logoUrl: '/assets/logos/Build-It-Logo-RED.png',
      companyName: 'COMPANY NAME',
    },
    {
      description: 'This is a very long description text exactly 50 chars.',
      barcode: '978342941885',
      itemCode: 'ITEM-003',
      price: '10,000.00',
      binLoc: 'E5-F6',
      suppCode: '1234567890qwerty',
      date: new Date().toLocaleDateString('en-GB',
        {day: '2-digit', month: '2-digit', year: 'numeric'}),
      logo: 'COMPANY NAME',
      logoUrl: '/assets/logos/Build-It-Logo-RED.png',
      companyName: 'COMPANY NAME',
    },
  ];
  variables: ILabelVariable[] = [
    {
      id: 'description',
      name: 'Description',
      value: '{{description}}',
      x: 0,
      y: 0,
      fontSize: 14,
      width: 200,
      height: 20,
      type: 'text',
      fontWeight: 'normal',
      fontStyle: 'normal',
      textAlign: 'left',
      verticalAlign: 'middle',
      overflow: 'wrap',
    },
    {
      id: 'barcode',
      name: 'Barcode Number',
      value: '{{barcode}}',
      x: 0,
      y: 0,
      fontSize: 12,
      width: 100,
      height: 30,
      type: 'text',
      fontWeight: 'normal',
      fontStyle: 'normal',
      textAlign: 'left',
      verticalAlign: 'middle',
      overflow: 'wrap',
    },
    {
      id: 'itemCode',
      name: 'Item Code',
      value: '{{itemCode}}',
      x: 0,
      y: 0,
      fontSize: 14,
      width: 100,
      height: 30,
      type: 'text',
      fontWeight: 'normal',
      fontStyle: 'normal',
      textAlign: 'left',
      verticalAlign: 'middle',
      overflow: 'wrap',
    },
    {
      id: 'price',
      name: 'Price',
      value: '{{price}}',
      x: 0,
      y: 0,
      fontSize: 16,
      width: 80,
      height: 30,
      type: 'text',
      fontWeight: 'normal',
      fontStyle: 'normal',
      textAlign: 'left',
      verticalAlign: 'middle',
      overflow: 'wrap',
    },
    {
      id: 'suppCode',
      name: 'Supplier Stock Code',
      value: '{{suppCode}}',
      x: 0,
      y: 0,
      fontSize: 16,
      width: 80,
      height: 30,
      type: 'text',
      fontWeight: 'normal',
      fontStyle: 'normal',
      textAlign: 'left',
      verticalAlign: 'middle',
      overflow: 'wrap',
    },
    {
      id: 'binLoc',
      name: 'Bin Location',
      value: '{{binLoc}}',
      x: 0,
      y: 0,
      fontSize: 12,
      width: 80,
      height: 30,
      type: 'text',
      fontWeight: 'normal',
      fontStyle: 'normal',
      textAlign: 'left',
      verticalAlign: 'middle',
      overflow: 'wrap',
    },
    {
      id: 'companyName',
      name: 'Company Name',
      value: '{{companyName}}',
      x: 0,
      y: 0,
      fontSize: 14,
      width: 100,
      height: 30,
      type: 'text',
      fontWeight: 'normal',
      fontStyle: 'normal',
      textAlign: 'left',
      verticalAlign: 'middle',
      overflow: 'wrap',
    },
    {
      id: 'date',
      name: 'Date',
      value: '{{date}}',
      x: 0,
      y: 0,
      fontSize: 12,
      width: 100,
      height: 30,
      type: 'text',
      fontWeight: 'normal',
      fontStyle: 'normal',
      textAlign: 'left',
      verticalAlign: 'middle',
      overflow: 'wrap',
    },
    {
      id: 'logo',
      name: 'Logo',
      value: '{{logo}}',
      x: 0,
      y: 0,
      fontSize: 16,
      width: 100,
      height: 30,
      type: 'image',
      isLogo: true,
      fontWeight: 'bold',
      fontStyle: 'normal',
      textAlign: 'left',
      verticalAlign: 'middle',
      overflow: 'wrap',
      imageUrl: '',
    },
    {
      id: 'barcodeImage',
      name: 'Barcode Image',
      value: '{{barcodeGraphic}}',
      x: 0,
      y: 0,
      fontSize: 0,
      width: 150,
      height: 60,
      type: 'image',
      imageUrl: '',
      fontWeight: 'normal',
      fontStyle: 'normal',
      textAlign: 'left',
      verticalAlign: 'middle',
      overflow: 'wrap',
    },
    {
      id: 'test',
      name: 'Test Label Size',
      value: 'TEST LABEL SIZE',
      x: 0,
      y: 0,
      fontSize: 14,
      width: 100,
      height: 30,
      type: 'text',
      fontWeight: 'normal',
      fontStyle: 'normal',
      textAlign: 'center',
      verticalAlign: 'middle',
      overflow: 'wrap',
      showBorder: true,
    },
  ];
  labelItems: ILabelVariable[] = [];
  selectedItem: ILabelVariable | null = null;
  showFormatSidebar = false;
  savedLayouts: ILayout[] = [];
  currentLayoutName = 'Default Layout';
  showLayoutDialog = false;
  selectedStore$: Observable<IStore>;
  enableGridSnap = true;
  hasUnsavedChanges = false;
  currencySymbol = 'R';
  /**
   * Names currently on the clipboard (for UI bindings). Full layout data lives in the clipboard service.
   */
  copiedLayoutNames: string[] = [];
  isPastingLayouts = false;
  protected readonly iconColourEnum = IconColourEnum;
  protected readonly icons = IconsEnum;
  private ctx!: CanvasRenderingContext2D;
  private previewBarcodeCache = new Map<string, HTMLImageElement>();
  private previewLogoCache = new Map<string, HTMLImageElement>();
  private barcodeCache = new Map<string, string>();
  private readonly dpi = 96;
  private readonly mmToPixels = this.dpi / 25.4;  // 25.4mm = 1 inch
  private readonly defaultLogoData = '/assets/logos/Build-It-Logo-RED.png';
  private isResizing = false;
  private resizeStartX = 0;
  private resizeStartY = 0;
  private resizeStartWidth = 0;
  private resizeStartHeight = 0;
  private isDragging = false;
  private dragStartX = 0;
  private dragStartY = 0;
  private itemStartX = 0;
  private itemStartY = 0;
  private boundHandleWindowResize: typeof this.handleWindowResize = this.handleWindowResize.bind(this);
  private resizeHandle: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | null = null;
  private readonly dragOffTooltip: HTMLElement | null = null;
  private resizeTimeout: ReturnType<typeof setTimeout>;
  private pendingStoreChange: IStore | null = null;
  //endregion
  private storeData = '';
  private companyName = '';
  private fromStore = true;

  constructor(
    private collectionStoresSettingsService: CollectionStoresSettingsService,
    private collectionGlobalService: CollectionGlobalService,
    private readonly store: Store,
    private modalController: ModalControllerService,
    private readonly layoutClipboard: ShelfTalkerLayoutClipboardService,
  ) {
    this.dragOffTooltip = document.createElement('div');
    this.dragOffTooltip.className = 'drag-off-tooltip';
    this.dragOffTooltip.textContent = 'Release to remove from canvas';
    document.body.appendChild(this.dragOffTooltip);
    this.syncClipboardNamesFromService();
  }

  //#region getters and setters
  get labelWidth(): number {
    return Math.round(this.labelWidthMm * this.mmToPixels);
  }

  get labelHeight(): number {
    return Math.round(this.labelHeightMm * this.mmToPixels);
  }

  get zoomedLabelWidth(): number {
    return this.labelWidth * this.zoomLevel;
  }

  get zoomedLabelHeight(): number {
    return this.labelHeight * this.zoomLevel;
  }

  get canCopyLayoutsFromStore(): boolean {
    return this.fromStore;
  }

  get singlePasteLayoutName(): string {
    if (this.copiedLayoutNames.length === 0) {
      return '';
    }
    return this.copiedLayoutNames[this.copiedLayoutNames.length - 1];
  }

  //endregion

  //#region lifeCycle Hooks

  @Input() set setSelectedStore(store: IStore) {
    if (this.selectedStore && this.selectedStore.storeId === store.storeId) {
      return;
    }

    if (this.hasUnsavedChanges) {
      void this.showUnsavedChangesConfirmation(store);
    } else {
      this.changeToStore(store);
    }
  }

  //endregion

  ngAfterViewInit(): void {
    void this.collectionStoresSettingsService
      .getDocument(
        createSharedPaths().setStoreId(this.selectedStore.storeId).storeInfo(),
      ).then(async (storeData): Promise<void> => {
        this.storeData = storeData['companyLogo'];
        this.companyName = storeData['companyName'] ?? '';
        const isValid = await isValidBase64(this.storeData);
        const logoUrl = isValid ? this.storeData : '/assets/logos/Build-It-Logo-RED.png';
        this.previewData.forEach((label: IPreviewData) => {
          label.logoUrl = logoUrl;
        });
      });
    this.selectedStore$ = this.store.select(selectSelectedStore);
    this.ctx = this.canvasRef.nativeElement.getContext('2d');

    this.canvasRef.nativeElement.width = this.zoomedLabelWidth;
    this.canvasRef.nativeElement.height = this.zoomedLabelHeight;

    this.barcodeCache = new Map<string, string>();
    this.previewBarcodeCache = new Map<string, HTMLImageElement>();

    this.drawGrid();
    void this.drawItems();
    this.calculatePreviewScale();

    window.addEventListener('resize', this.boundHandleWindowResize);
  }

  ngOnDestroy(): void {
    if (this.dragOffTooltip) {
      document.body.removeChild(this.dragOffTooltip);
    }
    window.removeEventListener('resize', this.boundHandleWindowResize);
  }

  //#region Canvas Drawing
  /**
   * Draws a grid on the canvas element.
   *
   * This method performs the following operations:
   * - Checks if the canvas reference exists; if not, it exits early.
   * - Retrieves the 2D drawing context from the canvas.
   * - Clears the canvas and fills it with a white background.
   * - Draws minor grid lines at intervals determined by the `mmToPixels` property using a light stroke.
   * - Draws major grid lines every 10 minor intervals using a darker stroke.
   * - If grid snapping is enabled (via the `enableGridSnap` property), renders a grid snap indicator
   *   in the bottom-right corner of the canvas.
   *
   * @returns {void} No return value.
   */
  public drawGrid(): void {
    if (!this.canvasRef) return;

    const canvas = this.canvasRef.nativeElement;
    this.ctx = canvas.getContext('2d');
    this.ctx.clearRect(0, 0, canvas.width, canvas.height);
    this.ctx.fillStyle = 'white';
    this.ctx.fillRect(0, 0, canvas.width, canvas.height);

    this.ctx.save();
    this.ctx.scale(this.zoomLevel, this.zoomLevel);

    const gridStepSize = this.mmToPixels;
    this.ctx.strokeStyle = '#c8d1db';
    this.ctx.lineWidth = 0.5;

    for (let x = 0; x <= this.labelWidth; x += gridStepSize) {
      this.ctx.beginPath();
      this.ctx.moveTo(x, 0);
      this.ctx.lineTo(x, this.labelHeight);
      this.ctx.stroke();
    }

    for (let y = 0; y <= this.labelHeight; y += gridStepSize) {
      this.ctx.beginPath();
      this.ctx.moveTo(0, y);
      this.ctx.lineTo(this.labelWidth, y);
      this.ctx.stroke();
    }

    this.ctx.strokeStyle = '#94a3b8';
    this.ctx.lineWidth = 1;

    for (let x = 0; x <= this.labelWidth; x += gridStepSize * 10) {
      this.ctx.beginPath();
      this.ctx.moveTo(x, 0);
      this.ctx.lineTo(x, this.labelHeight);
      this.ctx.stroke();
    }

    for (let y = 0; y <= this.labelHeight; y += gridStepSize * 10) {
      this.ctx.beginPath();
      this.ctx.moveTo(0, y);
      this.ctx.lineTo(this.labelWidth, y);
      this.ctx.stroke();
    }

    if (this.enableGridSnap) {
      const indicatorSize = 16;
      const padding = 8;
      this.ctx.fillStyle = 'white';
      this.ctx.fillRect(this.labelWidth - padding - indicatorSize, this.labelHeight - padding - indicatorSize,
        indicatorSize, indicatorSize);

      this.ctx.strokeStyle = 'black';
      this.ctx.lineWidth = 1;

      for (let i = 1; i < 4; i++) {
        const x = this.labelWidth - padding - indicatorSize + (i * indicatorSize / 4);
        this.ctx.beginPath();
        this.ctx.moveTo(x, this.labelHeight - padding - indicatorSize);
        this.ctx.lineTo(x, this.labelHeight - padding);
        this.ctx.stroke();
      }

      for (let i = 1; i < 4; i++) {
        const y = this.labelHeight - padding - indicatorSize + (i * indicatorSize / 4);
        this.ctx.beginPath();
        this.ctx.moveTo(this.labelWidth - padding - indicatorSize, y);
        this.ctx.lineTo(this.labelWidth - padding, y);
        this.ctx.stroke();
      }
    }

    this.ctx.restore();
  }

  /**
   * Renders label items onto the canvas.
   *
   * This asynchronous method performs the following tasks:
   * - Checks if the canvas reference is available; if not, the function returns immediately.
   * - Retrieves the 2D drawing context from the canvas.
   * - Iterates over each label item concurrently:
   *   - For the selected item, fills its area in white, outlines it with a highlighted blue border,
   *     and draws resize handles.
   *   - For non-selected items, fills the area in white and outlines it with a default gray border.
   * - For items with a value of '{{barcodeGraphic}}':
   *   - Generates a barcode image using `generateBarcodeImage` and attempts to draw it.
   *   - If the image is not immediately ready or if an error occurs, displays fallback text.
   * - For items of type 'image' marked as a logo (`isLogo` true):
   *   - Attempts to retrieve the logo from a cache.
   *   - If not cached, loads the image using `preloadImage` and caches it on successful load.
   *   - If loading fails, draws a logo placeholder.
   * - For non-image items:
   *   - Determines the display text, handling potential template placeholders.
   *   - Adjusts the font size if the text exceeds the available width when overflow mode is set to 'shrink'.
   *   - Positions the text based on horizontal (`textAlign`) and vertical (`verticalAlign`) alignment settings.
   *   - Either wraps the text using `updatePreviewText` or draws it directly.
   *
   * @async
   * @returns {Promise<void>} A promise that resolves when all items have been rendered onto the canvas.
   */
  public async drawItems(): Promise<void> {
    if (!this.canvasRef) return;

    const canvas: HTMLCanvasElement = this.canvasRef.nativeElement;
    this.ctx = canvas.getContext('2d');

    this.ctx.save();
    this.ctx.scale(this.zoomLevel, this.zoomLevel);

    await Promise.all(this.labelItems.map(async item => {
      if (item === this.selectedItem) {
        this.ctx.fillStyle = 'white';
        this.ctx.fillRect(item.x, item.y, item.width, item.height);
        this.ctx.strokeStyle = '#007bff';
        this.ctx.lineWidth = 2;
        this.ctx.strokeRect(item.x, item.y, item.width, item.height);
        this.drawResizeHandles(item);
      } else {
        this.ctx.fillStyle = 'white';
        this.ctx.fillRect(item.x, item.y, item.width, item.height);
        this.ctx.strokeStyle = '#ddd';
        this.ctx.lineWidth = 1;
        this.ctx.strokeRect(item.x, item.y, item.width, item.height);
      }

      if (item.value === '{{barcodeGraphic}}') {
        try {
          const barcodeDataUrl = await this.generateBarcodeImage('sample-123');
          this.ctx.fillStyle = '#f0f0f0';
          this.ctx.fillRect(item.x + 1, item.y + 1, item.width - 2, item.height - 2);

          const barcodeImage = new Image();
          barcodeImage.src = barcodeDataUrl;

          if (barcodeImage.complete) {
            this.ctx.drawImage(
              barcodeImage,
              0, 0, barcodeImage.width, barcodeImage.height,
              item.x + 5, item.y + 5, item.width - 10, item.height - 10,
            );
          } else {
            this.ctx.fillStyle = 'black';
            this.ctx.font = '12px Arial';
            this.ctx.textAlign = 'center';
            this.ctx.textBaseline = 'middle';
            this.ctx.fillText('Barcode Image', item.x + item.width / 2, item.y + item.height / 2);
          }
        } catch (error) {
          console.error('Error rendering barcode:', error);
          this.ctx.fillStyle = 'black';
          this.ctx.font = '12px Arial';
          this.ctx.textAlign = 'center';
          this.ctx.textBaseline = 'middle';
          this.ctx.fillText('Barcode Image', item.x + item.width / 2, item.y + item.height / 2);
        }
      } else if (item.type === 'image' && item.isLogo) {
        try {
          let logoImage = this.previewLogoCache.get(item.value);

          if (!logoImage) {
            if (item.value && item.value !== '{{logo}}') {
              try {
                logoImage = await this.preloadImage(item.value);

                if (logoImage?.complete && logoImage.naturalWidth > 0) {
                  this.previewLogoCache.set(item.value, logoImage);
                }

                this.ctx.drawImage(logoImage, item.x, item.y, item.width, item.height);
              } catch (error) {
                console.warn('Error in logo loading process, using placeholder', error);
                this.drawLogoPlaceholder(this.ctx, item);
              }
            } else {
              this.drawLogoPlaceholder(this.ctx, item);
            }
          } else {
            this.ctx.drawImage(logoImage, item.x, item.y, item.width, item.height);
          }
        } catch (error) {
          console.warn('Error in logo drawing process, using placeholder', error);
          this.drawLogoPlaceholder(this.ctx, item);
        }
      } else if (item.type !== 'image') {
        this.ctx.fillStyle = 'black';

        const displayText = item.value.includes('{{') ?
          item.value.replace(/\{\{([^}]+)\}\}/g, '$1') :
          item.name;

        let fontSize = item.fontSize;
        let textWidth = 0;
        const maxWidth = item.width - 8;

        if (item.overflow === 'shrink') {
          this.ctx.font = `${item.fontStyle} ${item.fontWeight} ${fontSize}px Arial`;
          textWidth = this.ctx.measureText(displayText).width;

          if (textWidth > maxWidth) {
            const minFontSize = 8;
            while (fontSize > minFontSize && textWidth > maxWidth) {
              fontSize -= 1;
              this.ctx.font = `${item.fontStyle} ${item.fontWeight} ${fontSize}px Arial`;
              textWidth = this.ctx.measureText(displayText).width;
            }
          }
        } else {
          this.ctx.font = `${item.fontStyle} ${item.fontWeight} ${fontSize}px Arial`;
        }

        let textX = item.x + 4;
        if (item.textAlign === 'center') {
          textX = item.x + item.width / 2;
          this.ctx.textAlign = 'center';
        } else if (item.textAlign === 'right') {
          textX = item.x + item.width - 4;
          this.ctx.textAlign = 'right';
        } else {
          this.ctx.textAlign = 'left';
        }

        let textY = item.y + 4;
        if (item.verticalAlign === 'middle') {
          textY = item.y + item.height / 2;
          this.ctx.textBaseline = 'middle';
        } else if (item.verticalAlign === 'bottom') {
          textY = item.y + item.height - 4;
          this.ctx.textBaseline = 'bottom';
        } else {
          this.ctx.textBaseline = 'top';
        }

        if (item.overflow === 'wrap') {
          this.updatePreviewText(this.ctx, item, displayText, textX, textY, fontSize, maxWidth);
        } else {
          this.ctx.fillText(displayText, textX, textY);
        }
      }
    }));

    this.ctx.restore();
  }

  async drawPreview(
    canvasElement: HTMLCanvasElement,
    data: IPreviewData,
    shouldScale: boolean = true,
    forceUpdate: boolean = false,
  ): Promise<void> {
    if (!canvasElement) return;

    const ctx = canvasElement.getContext('2d');
    if (!ctx) return;

    if (canvasElement.width !== this.labelWidth * this.previewScale ||
      canvasElement.height !== this.labelHeight * this.previewScale) {
      canvasElement.width = this.labelWidth * this.previewScale;
      canvasElement.height = this.labelHeight * this.previewScale;
    }

    ctx.clearRect(0, 0, canvasElement.width, canvasElement.height);

    if (shouldScale) {
      ctx.save();
      ctx.scale(this.previewScale, this.previewScale);
    }

    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, this.labelWidth, this.labelHeight);

    for (const item of this.labelItems) {
      ctx.fillStyle = 'white';
      ctx.fillRect(item.x, item.y, item.width, item.height);

      ctx.strokeStyle = '#ddd';
      ctx.lineWidth = 0.5;
      ctx.strokeRect(item.x, item.y, item.width, item.height);

      if (item.value === '{{barcodeGraphic}}') {
        try {
          this.previewBarcodeCache.delete(data.barcode);
          const barcodeDataUrl = await this.generateBarcodeImage(data.barcode);
          const barcodeImage = new Image();
          barcodeImage.src = barcodeDataUrl;

          await new Promise<void>(resolve => {
            barcodeImage.onload = (): void => {
              resolve();
            };
            setTimeout(resolve, 500);
          });

          ctx.drawImage(
            barcodeImage,
            0, 0,
            barcodeImage.width, barcodeImage.height,
            item.x, item.y,
            item.width, item.height,
          );
        } catch (error) {
          console.error('Error drawing barcode in preview:', error);
          ctx.fillStyle = 'black';
          ctx.font = '12px Arial';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText('Barcode Image', item.x + item.width / 2, item.y + item.height / 2);
        }
      } else if (item.type === 'image' && item.isLogo) {
        if (data.logoUrl && data.logoUrl.length > 0) {
          try {
            let logoImage = this.previewLogoCache.get(data.logoUrl);

            if (!logoImage || forceUpdate) {
              try {
                logoImage = await this.preloadImage(data.logoUrl);

                if (logoImage?.complete && logoImage.naturalWidth > 0) {
                  this.previewLogoCache.set(data.logoUrl, logoImage);
                }
                ctx.drawImage(logoImage, item.x, item.y, item.width, item.height);
              } catch (error) {
                console.warn('Error in preview logo loading, using placeholder', error);
                this.drawLogoPlaceholder(ctx, item);
              }
            } else {
              ctx.drawImage(logoImage, item.x, item.y, item.width, item.height);
            }
          } catch (error) {
            console.warn('Error in preview logo drawing, using placeholder', error);
            this.drawLogoPlaceholder(ctx, item);
          }
        } else {
          this.drawLogoPlaceholder(ctx, item);
        }
      } else if (item.type !== 'image') {
        let value = item.value;

        if (value.includes('{{description}}')) value = value.replace('{{description}}', data.description);
        if (value.includes('{{barcode}}')) value = value.replace('{{barcode}}', data.barcode);
        if (value.includes('{{itemCode}}')) value = value.replace('{{itemCode}}', data.itemCode);
        if (value.includes('{{price}}')) value = value.replace('{{price}}', `${this.currencySymbol}${data.price}`);
        if (value.includes('{{binLoc}}')) value = value.replace('{{binLoc}}', data.binLoc);
        if (value.includes('{{date}}')) value = value.replace('{{date}}', data.date);
        if (value.includes('{{logo}}')) value = value.replace('{{logo}}', data.logo);
        if (value.includes('{{suppCode}}')) value = value.replace('{{suppCode}}', data.suppCode);
        if (value.includes('{{companyName}}')) value = value.replace('{{companyName}}', data.companyName);

        ctx.fillStyle = 'black';

        let fontSize = item.fontSize;
        let textWidth = 0;
        const maxWidth = item.width - 8;

        if (item.overflow === 'shrink') {
          ctx.font = `${item.fontStyle} ${item.fontWeight} ${fontSize}px Arial`;
          textWidth = ctx.measureText(value).width;

          if (textWidth > maxWidth) {
            const minFontSize = 8;
            while (fontSize > minFontSize && textWidth > maxWidth) {
              fontSize -= 1;
              ctx.font = `${item.fontStyle} ${item.fontWeight} ${fontSize}px Arial`;
              textWidth = ctx.measureText(value).width;
            }
          }
        } else {
          ctx.font = `${item.fontStyle} ${item.fontWeight} ${fontSize}px Arial`;
          textWidth = ctx.measureText(value).width;
        }

        let textX = item.x + 4;
        if (item.textAlign === 'center') {
          textX = item.x + item.width / 2;
          ctx.textAlign = 'center';
        } else if (item.textAlign === 'right') {
          textX = item.x + item.width - 4;
          ctx.textAlign = 'right';
        } else {
          ctx.textAlign = 'left';
        }

        let textY = item.y + 4;
        if (item.verticalAlign === 'middle') {
          textY = item.y + item.height / 2;
          ctx.textBaseline = 'middle';
        } else if (item.verticalAlign === 'bottom') {
          textY = item.y + item.height - 4;
          ctx.textBaseline = 'bottom';
        } else {
          ctx.textBaseline = 'top';
        }

        if (item.overflow === 'wrap') {
          this.updatePreviewText(ctx, item, value, textX, textY, fontSize, maxWidth);
        } else {
          ctx.fillText(value, textX, textY);
        }
      }
    }

    if (shouldScale) {
      ctx.restore();
    }
  }

  updateLabelSize(onload: boolean = true): void {
    if (this.canvasRef) {
      const oldWidth = this.canvasRef.nativeElement.width;
      const oldHeight = this.canvasRef.nativeElement.height;
      const widthRatio = this.zoomedLabelWidth / oldWidth;
      const heightRatio = this.zoomedLabelHeight / oldHeight;

      this.canvasRef.nativeElement.width = this.zoomedLabelWidth;
      this.canvasRef.nativeElement.height = this.zoomedLabelHeight;

      if (oldWidth > 0 && oldHeight > 0 && !onload) {
        this.labelItems.forEach(item => {
          item.x = item.x * widthRatio;
          item.y = item.y * heightRatio;
          item.width = item.width * widthRatio;
          item.height = item.height * heightRatio;
        });
      }

      if (!onload) {
        this.hasUnsavedChanges = true;
      }

      this.calculatePreviewScale();

      this.drawGrid();
      void this.drawItems();

    }
  }

  updateCurrencySymbol(): void {
    this.hasUnsavedChanges = true;
    this.drawGrid();
    void this.drawItems();
  }

  setFontWeight(weight: 'normal' | 'bold'): void {
    if (this.selectedItem) {
      this.selectedItem.fontWeight = weight;
      this.drawGrid();
      void this.drawItems();
    }
  }

  setFontStyle(style: 'normal' | 'italic'): void {
    if (this.selectedItem) {
      this.selectedItem.fontStyle = style;
      this.drawGrid();
      void this.drawItems();

    }
  }

  setTextAlign(align: 'left' | 'center' | 'right'): void {
    if (this.selectedItem) {
      this.selectedItem.textAlign = align;
      this.drawGrid();
      void this.drawItems();

    }
  }

  alignHorizontally(): void {
    if (this.selectedItem) {
      let x = (this.labelWidth - this.selectedItem.width) / 2;

      if (this.enableGridSnap) {
        x = this.snapToGrid(x);
      }

      this.selectedItem.x = x;
      this.drawGrid();
      void this.drawItems();
    }
  }

  alignVertically(): void {
    if (this.selectedItem) {
      let y = (this.labelHeight - this.selectedItem.height) / 2;

      if (this.enableGridSnap) {
        y = this.snapToGrid(y);
      }

      this.selectedItem.y = y;
      this.drawGrid();
      void this.drawItems();
    }
  }

  deleteSelectedItem(): void {
    if (!this.selectedItem) return;

    const index = this.labelItems.findIndex(item => item === this.selectedItem);
    if (index !== -1) {
      if (!this.variables.some(v => v.id === this.selectedItem.id)) {
        const variableItem = {...this.selectedItem};
        variableItem.x = 0;
        variableItem.y = 0;
        variableItem.width = 100;
        variableItem.height = 30;
        variableItem.fontSize = 14;
        variableItem.fontWeight = 'normal';
        variableItem.fontStyle = 'normal';
        variableItem.textAlign = 'left';
        variableItem.verticalAlign = 'top';
        variableItem.overflow = 'wrap';

        this.variables.push(variableItem);
      }

      this.labelItems.splice(index, 1);
      this.selectedItem = null;
      this.showFormatSidebar = false;

      this.drawGrid();
      void this.drawItems();
    }
  }

  public onOverflowSelectionChange(): void {
    if (this.selectedItem) {
      this.drawGrid();
      void this.drawItems();
    }
  }

  public toggleGridSnap(): void {
    this.enableGridSnap = !this.enableGridSnap;
    this.drawGrid();
    void this.drawItems();
  }

  //endregion

  public resetLabelDesigner(): void {
    this.labelItems = [];

    this.selectedItem = null;
    this.showFormatSidebar = false;

    this.barcodeCache = new Map<string, string>();
    this.previewBarcodeCache = new Map<string, HTMLImageElement>();
    this.previewLogoCache = new Map<string, HTMLImageElement>();

    this.currentLayoutName = 'Default Layout';

    this.canvasRef.nativeElement.width = this.zoomedLabelWidth;
    this.canvasRef.nativeElement.height = this.zoomedLabelHeight;

    this.drawGrid();
    void this.drawItems();

    this.hasUnsavedChanges = false;
  }

  //#region Event Handlers
  @HostListener('window:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    if (!this.selectedItem) return;

    if (event.key === 'Delete') {
      event.preventDefault();
      this.deleteSelectedItem();
    }

    if (this.enableGridSnap && ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.key)) {
      event.preventDefault();

      const gridStep = this.mmToPixels;

      switch (event.key) {
        case 'ArrowLeft':
          this.selectedItem.x = Math.max(0, this.selectedItem.x - gridStep);
          break;
        case 'ArrowRight':
          this.selectedItem.x = Math.min(
            this.labelWidth - this.selectedItem.width,
            this.selectedItem.x + gridStep,
          );
          break;
        case 'ArrowUp':
          this.selectedItem.y = Math.max(0, this.selectedItem.y - gridStep);
          break;
        case 'ArrowDown':
          this.selectedItem.y = Math.min(
            this.labelHeight - this.selectedItem.height,
            this.selectedItem.y + gridStep,
          );
          break;
      }

      this.selectedItem.x = this.snapToGrid(this.selectedItem.x);
      this.selectedItem.y = this.snapToGrid(this.selectedItem.y, false);

      this.drawGrid();
      void this.drawItems();
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.selectedItem || !this.showFormatSidebar) return;

    if (this.isDragging || this.isResizing) return;

    const formatSidebar = document.querySelector('.format-sidebar');
    const canvas = this.canvasRef?.nativeElement;

    const isClickOnSelect = (event.target as Element)?.closest('select') !== null;
    const isClickOnOption = (event.target as Element)?.closest('option') !== null;
    const isClickOnOverlay = (event.target as Element)?.closest('.cdk-overlay-container') !== null;

    if (isClickOnSelect || isClickOnOption || isClickOnOverlay) {
      return;
    }

    const isClickInsideSidebar = formatSidebar?.contains(event.target as Node);
    const isClickInsideCanvas = canvas?.contains(event.target as Node);

    if (isClickInsideCanvas) return;

    if (!isClickInsideSidebar) {
      this.selectedItem = null;
      this.showFormatSidebar = false;

      this.drawGrid();
      void this.drawItems();
    }
  }

  onCanvasMouseDown(event: MouseEvent): void {
    if (!this.canvasRef) return;

    const canvas = this.canvasRef.nativeElement;
    const rect = canvas.getBoundingClientRect();
    const x = (event.clientX - rect.left) / this.zoomLevel;
    const y = (event.clientY - rect.top) / this.zoomLevel;

    if (this.selectedItem) {
      const handle = this.getResizeHandle(x, y);
      if (handle) {
        this.isResizing = true;
        this.resizeStartX = x;
        this.resizeStartY = y;
        this.resizeStartWidth = this.selectedItem.width;
        this.resizeStartHeight = this.selectedItem.height;
        this.resizeHandle = handle;

        if (handle === 'top-left' || handle === 'bottom-right') {
          document.body.style.cursor = 'nwse-resize';
        } else {
          document.body.style.cursor = 'nesw-resize';
        }

        return;
      }
    }

    const item = this.getItemAtPosition(x, y);

    if (item) {
      this.selectedItem = item;
      this.isDragging = true;
      this.dragStartX = x;
      this.dragStartY = y;
      this.itemStartX = item.x;
      this.itemStartY = item.y;
      document.body.style.cursor = 'move';

      if (!this.showFormatSidebar) {
        this.showFormatSidebar = true;
      }
    } else {
      this.selectedItem = null;
      this.showFormatSidebar = false;
    }

    this.drawGrid();
    void this.drawItems();
  }

  onCanvasMouseMove(event: MouseEvent): void {
    if (!this.canvasRef) return;

    const canvas = this.canvasRef.nativeElement;
    const rect = canvas.getBoundingClientRect();
    const x = (event.clientX - rect.left) / this.zoomLevel;
    const y = (event.clientY - rect.top) / this.zoomLevel;

    if (this.isResizing && this.selectedItem && this.resizeHandle) {
      const dx = x - this.resizeStartX;
      const dy = y - this.resizeStartY;

      switch (this.resizeHandle) {
        case 'top-left':
          let newX = this.itemStartX + dx;
          let newY = this.itemStartY + dy;
          let newWidth = this.resizeStartWidth - dx;
          let newHeight = this.resizeStartHeight - dy;

          if (this.enableGridSnap) {
            newX = this.snapToGrid(newX);
            newY = this.snapToGrid(newY);
            newWidth = this.resizeStartWidth - (newX - this.itemStartX);
            newHeight = this.resizeStartHeight - (newY - this.itemStartY);
          }

          this.selectedItem.x = newX;
          this.selectedItem.y = newY;
          this.selectedItem.width = newWidth;
          this.selectedItem.height = newHeight;
          break;

        case 'top-right':
          let newYTopRight = this.itemStartY + dy;
          let newWidthTopRight = this.resizeStartWidth + dx;
          let newHeightTopRight = this.resizeStartHeight - dy;

          if (this.enableGridSnap) {
            newYTopRight = this.snapToGrid(newYTopRight, false);
            newWidthTopRight = this.snapToGrid(newWidthTopRight);
            newHeightTopRight = this.resizeStartHeight - (newYTopRight - this.itemStartY);
          }

          this.selectedItem.y = newYTopRight;
          this.selectedItem.width = newWidthTopRight;
          this.selectedItem.height = newHeightTopRight;
          break;

        case 'bottom-left':
          let newXBottomLeft = this.itemStartX + dx;
          let newWidthBottomLeft = this.resizeStartWidth - dx;
          let newHeightBottomLeft = this.resizeStartHeight + dy;

          if (this.enableGridSnap) {
            newXBottomLeft = this.snapToGrid(newXBottomLeft);
            newHeightBottomLeft = this.snapToGrid(newHeightBottomLeft, false);
            newWidthBottomLeft = this.resizeStartWidth - (newXBottomLeft - this.itemStartX);
          }

          this.selectedItem.x = newXBottomLeft;
          this.selectedItem.width = newWidthBottomLeft;
          this.selectedItem.height = newHeightBottomLeft;
          break;

        case 'bottom-right':
          let newWidthBottomRight = this.resizeStartWidth + dx;
          let newHeightBottomRight = this.resizeStartHeight + dy;

          if (this.enableGridSnap) {
            newWidthBottomRight = this.snapToGrid(newWidthBottomRight);
            newHeightBottomRight = this.snapToGrid(newHeightBottomRight, false);
          }

          this.selectedItem.width = newWidthBottomRight;
          this.selectedItem.height = newHeightBottomRight;
          break;
      }

      this.selectedItem.width = Math.max(20, this.selectedItem.width);
      this.selectedItem.height = Math.max(20, this.selectedItem.height);

      this.drawGrid();
      void this.drawItems();
      return;
    }

    if (this.isDragging && this.selectedItem) {
      const dx = x - this.dragStartX;
      const dy = y - this.dragStartY;

      let newX = this.itemStartX + dx;
      let newY = this.itemStartY + dy;

      if (this.enableGridSnap) {
        newX = this.snapToGrid(newX);
        newY = this.snapToGrid(newY, false);
      }

      this.selectedItem.x = newX;
      this.selectedItem.y = newY;

      const isOutsideCanvas = (
        this.selectedItem.x + this.selectedItem.width < 0 ||
        this.selectedItem.y + this.selectedItem.height < 0 ||
        this.selectedItem.x > this.labelWidth ||
        this.selectedItem.y > this.labelHeight
      );

      if (isOutsideCanvas) {
        document.body.classList.add('dragging-off-canvas');
        canvas.parentElement?.classList.add('canvas-drag-active');

        if (this.dragOffTooltip) {
          this.dragOffTooltip.classList.add('active');
          this.dragOffTooltip.style.left = `${event.clientX + 15}px`;
          this.dragOffTooltip.style.top = `${event.clientY + 15}px`;
        }
      } else {
        document.body.classList.remove('dragging-off-canvas');
        canvas.parentElement?.classList.remove('canvas-drag-active');

        if (this.dragOffTooltip) {
          this.dragOffTooltip.classList.remove('active');
        }
      }

      this.drawGrid();
      void this.drawItems();
    }
  }

  onCanvasMouseUp(event: MouseEvent): void {
    if (!this.canvasRef) return;

    const canvas = this.canvasRef.nativeElement;
    const rect = canvas.getBoundingClientRect();
    const x = (event.clientX - rect.left) / this.zoomLevel;
    const y = (event.clientY - rect.top) / this.zoomLevel;

    if (this.isDragging && this.selectedItem) {
      const mouseOutsideCanvas =
        event.clientX < rect.left ||
        event.clientX > rect.right ||
        event.clientY < rect.top ||
        event.clientY > rect.bottom;

      const itemOutsideCanvas = (
        this.selectedItem.x + this.selectedItem.width < 0 ||
        this.selectedItem.y + this.selectedItem.height < 0 ||
        this.selectedItem.x > this.labelWidth ||
        this.selectedItem.y > this.labelHeight
      );

      if (mouseOutsideCanvas || itemOutsideCanvas) {
        const index = this.labelItems.findIndex(item => item === this.selectedItem);
        if (index !== -1) {
          if (!this.variables.some((labelVariable: ILabelVariable) => labelVariable.id === this.selectedItem.id)) {
            const variableItem = {...this.selectedItem};
            variableItem.x = 0;
            variableItem.y = 0;
            variableItem.width = 100;
            variableItem.height = 30;
            variableItem.fontSize = 14;
            variableItem.fontWeight = 'normal';
            variableItem.fontStyle = 'normal';
            variableItem.textAlign = 'left';
            variableItem.verticalAlign = 'top';
            variableItem.overflow = 'wrap';

            this.variables.push(variableItem);
          }

          this.labelItems.splice(index, 1);
          this.selectedItem = null;
          this.showFormatSidebar = false;

          this.hasUnsavedChanges = true;
        }
      } else if (this.isDragging || this.isResizing) {
        this.hasUnsavedChanges = true;
      }
    }

    this.isDragging = false;
    this.isResizing = false;
    this.resizeHandle = null;
    document.body.style.cursor = 'default';
    document.body.classList.remove('dragging-off-canvas');
    canvas.parentElement?.classList.remove('canvas-drag-active');

    if (this.dragOffTooltip) {
      this.dragOffTooltip.classList.remove('active');
    }

    this.drawGrid();
    void this.drawItems();
  }

  onCanvasMouseLeave(): void {
    if (this.isDragging || this.isResizing) {
      this.isDragging = false;
      this.isResizing = false;
      this.resizeHandle = null;
      document.body.style.cursor = 'default';
      document.body.classList.remove('dragging-off-canvas');
      const canvas = this.canvasRef.nativeElement;
      canvas.parentElement?.classList.remove('canvas-drag-active');
      if (this.dragOffTooltip) {
        this.dragOffTooltip.classList.remove('active');
      }
      this.drawGrid();
      void this.drawItems();
    }
  }

  //endregion

  //#region save and load

  onDrop(event: CdkDragDrop<ILabelVariable[]>): void {
    if (event.previousContainer === event.container) {
      return;
    } else {
      const item = event.previousContainer.data[event.previousIndex];

      const newItem: ILabelVariable = {...item};

      const canvas = this.canvasRef.nativeElement;
      const rect = canvas.getBoundingClientRect();

      let x = this.labelWidth / 2 - newItem.width / 2;
      let y = this.labelHeight / 2 - newItem.height / 2;

      if (this.enableGridSnap) {
        x = this.snapToGrid(x);
        y = this.snapToGrid(y);
      }

      newItem.x = x;
      newItem.y = y;

      this.labelItems.push(newItem);
      this.selectedItem = newItem;
      this.showFormatSidebar = true;

      this.drawGrid();
      void this.drawItems();

      this.hasUnsavedChanges = true;
    }
  }

  async setLabelAsDefault(): Promise<void> {
    const confirmProps: ISharedModalBasic = {
      buttonAccept: true,
      buttonAcceptText: 'Confirm',
      buttonClose: true,
      buttonCloseCross: false,
      buttonCloseText: 'Cancel',
      modalTitle: 'Set Default Layout',
      modalTitleIcon: this.icons.checkAll,
      contextHeading: 'Are you sure you want to set this layout as the default?',
      modalTitleIconColor: this.iconColourEnum.primary,
      contextDescription: [
        'This layout will be set as the default for your store, and it will be applied to all shelf talkers by default.',
        'However, if a user later changes the selection, their choice will take precedence over this default.',
      ],
    };


    const confirmModal = await this.modalController.create({
      component: SharedModalBasicComponent,
      componentProps: confirmProps,
      cssClass: ['shared-basic-modal-css'],
    });

    await confirmModal.present();
    const modalResponse: IModalDismissResult<ISharedModalBasicResponse> = await confirmModal.onDidDismiss();

    if (modalResponse?.data?.buttonPressed === 'button-accept') {
      await this.collectionStoresSettingsService.setDocument(
        path_stores_storeId_settings_shelf_talkers_settings(this.selectedStore.storeId),
        {defaultLayout: this.currentLayoutName},
      );
    } else {
      return;
    }
  }

  openLayoutDialog(): void {
    this.showLayoutDialog = true;
  }


  async onCopyLayouts(layoutNames: string[]): Promise<void> {
    if (!this.fromStore || !this.selectedStore?.storeId) {
      return;
    }
    if (this.hasUnsavedChanges) {
      await this.showMustSaveBeforeCopyModal();
      return;
    }
    if (!layoutNames.length) {
      return;
    }

    const layoutsToCopy = this.savedLayouts.filter((layout: ILayout) => layoutNames.includes(layout.name));
    if (layoutsToCopy.length === 0) {
      return;
    }

    this.layoutClipboard.setLayouts(layoutsToCopy, this.selectedStore.storeId);
    this.syncClipboardNamesFromService();
    this.showLayoutDialog = false;

    const count = layoutsToCopy.length;
    await this.showInfoModal(
      'Layouts Copied',
      count === 1
        ? `"${layoutsToCopy[0].name}" copied. Switch store, then paste.`
        : `${count} layouts copied. Switch store, then paste.`,
      this.iconColourEnum.green,
    );
  }

  /**
   * Pastes every layout currently on the clipboard into the selected store.
   */
  async onPasteAllLayouts(): Promise<void> {
    const layouts = this.layoutClipboard.getLayouts();
    if (layouts.length === 0 || !this.selectedStore?.storeId || this.isPastingLayouts) {
      return;
    }
    if (this.hasUnsavedChanges) {
      await this.showMustSaveBeforeCopyModal();
      return;
    }

    this.isPastingLayouts = true;
    try {
      const confirmed = await this.confirmPasteLayouts(layouts);
      if (!confirmed) {
        return;
      }

      const overwritten = await this.mergeLayoutsIntoCurrentStore(layouts);
      await this.loadSavedLayouts();
      this.store.dispatch(getShelfTalkerSettings());
      this.showLayoutDialog = false;
      await this.showPasteSuccessModal(layouts.length, overwritten);
    } catch (error) {
      console.error('Error pasting layouts:', error);
      await this.showInfoModal(
        'Paste Failed',
        'Failed to paste layouts. Please try again.',
        this.iconColourEnum.red,
      );
    } finally {
      this.isPastingLayouts = false;
    }
  }

  /**
   * Pastes only the most recently copied layout into the selected store.
   */
  async onPasteOneLayout(): Promise<void> {
    const layout = this.layoutClipboard.getLastLayout();
    if (!layout || !this.selectedStore?.storeId || this.isPastingLayouts) {
      return;
    }
    if (this.hasUnsavedChanges) {
      await this.showMustSaveBeforeCopyModal();
      return;
    }

    this.isPastingLayouts = true;
    try {
      const confirmed = await this.confirmPasteLayouts([layout]);
      if (!confirmed) {
        return;
      }

      const overwritten = await this.mergeLayoutsIntoCurrentStore([layout]);
      await this.loadSavedLayouts();
      this.store.dispatch(getShelfTalkerSettings());
      await this.showPasteSuccessModal(1, overwritten);
    } catch (error) {
      console.error('Error pasting layout:', error);
      await this.showInfoModal(
        'Paste Failed',
        'Failed to paste layout. Please try again.',
        this.iconColourEnum.red,
      );
    } finally {
      this.isPastingLayouts = false;
    }
  }

  private syncClipboardNamesFromService(): void {
    this.copiedLayoutNames = this.layoutClipboard.getLayouts().map((layout: ILayout) => layout.name);
  }

  private async showMustSaveBeforeCopyModal(): Promise<void> {
    await this.showInfoModal(
      'Unsaved Changes',
      'Save or discard your layout changes before copying or pasting layouts.',
      this.iconColourEnum.yellow,
    );
  }

  private async showInfoModal(title: string, description: string, iconColour: IconColourEnum): Promise<void> {
    const props: ISharedModalBasic = {
      buttonAccept: false,
      buttonAcceptText: '',
      buttonClose: true,
      buttonCloseCross: false,
      buttonCloseText: 'Close',
      modalTitle: title,
      modalTitleIcon: IconsEnum.infoSlabCircleOutline,
      modalTitleIconColor: iconColour,
      contextDescription: [description],
    };
    const modal = await this.modalController.create({
      component: SharedModalBasicComponent,
      componentProps: props,
      cssClass: ['shared-basic-modal-css-small'],
      backdropDismiss: false,
    });
    await modal.present();
  }

  private async confirmPasteLayouts(layouts: ILayout[]): Promise<boolean> {
    const existingDoc = await this.collectionStoresSettingsService.getDocumentOrNull<IFirebaseLabelDesignsData>(
      path_stores_storeId_settings_shelf_talkers_settings(this.selectedStore.storeId),
    );
    const existingNames = new Set(
      (existingDoc?.layouts ?? []).map((layout: ILayout) => layout.name),
    );

    const overwritten = layouts.filter((layout: ILayout) => existingNames.has(layout.name)).map((l: ILayout) => l.name);
    const count = layouts.length;
    const contextDescription: string[] =
      overwritten.length > 0
        ? [
            count === 1
              ? `Paste "${layouts[0].name}" into this store?`
              : `Paste ${count} layouts into this store?`,
            '',
            'These layouts already exist and will be overwritten:',
            ...overwritten.map((name: string) => `• ${name}`),
          ]
        : [
            count === 1
              ? `Paste "${layouts[0].name}" into this store?`
              : `Paste ${count} layouts into this store?`,
          ];

    const props: ISharedModalBasic = {
      buttonAccept: true,
      buttonAcceptText: overwritten.length > 0 ? 'Overwrite & Paste' : 'Paste',
      buttonClose: true,
      buttonCloseCross: false,
      buttonCloseText: 'Cancel',
      modalTitle: 'Paste Layouts',
      modalTitleIcon: IconsEnum.clipboardCheckOutline,
      modalTitleIconColor: overwritten.length > 0 ? this.iconColourEnum.yellow : this.iconColourEnum.primary,
      contextHeading: overwritten.length > 0 ? 'Some layouts will be overwritten' : 'Confirm paste',
      contextDescription,
    };

    const modal = await this.modalController.create({
      backdropDismiss: false,
      component: SharedModalBasicComponent,
      componentProps: props,
      cssClass: ['shared-basic-modal-css-small'],
    });
    await modal.present();
    const response: IModalDismissResult<ISharedModalBasicResponse> = await modal.onDidDismiss();
    return response?.data?.buttonPressed === 'button-accept';
  }

  private async showPasteSuccessModal(pastedCount: number, overwritten: string[]): Promise<void> {
    const description =
      overwritten.length > 0
        ? pastedCount === 1
          ? `Layout pasted. Overwrote existing layout "${overwritten[0]}".`
          : `${pastedCount} layouts pasted. Overwrote ${overwritten.length}: ${overwritten.join(', ')}.`
        : pastedCount === 1
          ? 'Layout pasted successfully.'
          : `${pastedCount} layouts pasted successfully.`;

    await this.showInfoModal('Layouts Pasted', description, this.iconColourEnum.green);
  }

  /**
   * Merges pasted layouts into the current store settings document by name (overwrite).
   * Throws when the store settings read or write fails so callers can show a failure modal.
   */
  private async mergeLayoutsIntoCurrentStore(layoutsToPaste: ILayout[]): Promise<string[]> {
    const path = path_stores_storeId_settings_shelf_talkers_settings(this.selectedStore.storeId);
    const existing = await this.collectionStoresSettingsService.getDocumentOrNull<IFirebaseLabelDesignsData>(path);
    // Only merge against Firestore. Never seed from in-memory savedLayouts (can be stale after store switch).
    const targetLayouts: ILayout[] = (existing?.layouts ?? []).map((layout: ILayout) => structuredClone(layout));

    const overwritten: string[] = [];
    for (const incoming of layoutsToPaste) {
      const cloned: ILayout = structuredClone(incoming);
      cloned.timestamp = Date.now();
      const index = targetLayouts.findIndex((layout: ILayout) => layout.name === cloned.name);
      if (index >= 0) {
        targetLayouts[index] = cloned;
        overwritten.push(cloned.name);
      } else {
        targetLayouts.push(cloned);
      }
    }

    await this.collectionStoresSettingsService.setDocumentOrThrow(path, {layouts: targetLayouts});
    this.fromStore = true;
    return overwritten;
  }

  async loadSavedLayouts(): Promise<void> {
    this.savedLayouts = [];
    this.currentLayoutName = '';
    let selectedLayoutName: string | undefined;

    try {
      const layoutData = await this.collectionStoresSettingsService.getDocument<IFirebaseLabelDesignsData>(
        path_stores_storeId_settings_shelf_talkers_settings(this.selectedStore.storeId),
      );

      if (layoutData) {
        this.savedLayouts = layoutData['layouts'] || [];
        selectedLayoutName = layoutData['defaultLayout'] ?? (layoutData['layouts']?.[0]?.name ?? undefined);
        if (this.savedLayouts.length > 0) {
          this.fromStore = true;
        }
      }

      if (!this.savedLayouts || this.savedLayouts.length === 0) {
        this.fromStore = false;
        this.resetLabelDesigner();
        const globalLayoutData = await this.collectionGlobalService.getDocument(
          createGlobalPaths().defaultShelftalkerLabels(),
        );
        this.savedLayouts = globalLayoutData['layouts'] || [];
        selectedLayoutName = this.savedLayouts.length > 0 ? this.savedLayouts[0].name : undefined;
      }

      if (selectedLayoutName) {
        this.currentLayoutName = selectedLayoutName;
        const selectedLayout = this.savedLayouts?.find((layout: {
          name: string,
          data: ILabelDesign,
          timestamp: number
        }) => layout.name === selectedLayoutName);
        if (selectedLayout) {
          const layoutDetails = selectedLayout.data;
          this.labelWidthMm = layoutDetails.labelWidthMm;
          this.labelHeightMm = layoutDetails.labelHeightMm;
          this.labelItems = layoutDetails.labelItems;
          this.currencySymbol = layoutDetails.currencySymbol ? layoutDetails.currencySymbol : 'R';

          void Promise.resolve().then(() => {
            this.updateLabelSize();
          });
        }
      }
    } catch (error) {
      console.error('Error loading saved layouts:', error);
    }
    this.hasUnsavedChanges = false;
  }

  async handleSaveLayout(layoutName: string, store: IStore): Promise<void> {
    const layoutData = {
      labelItems: this.labelItems,
      labelWidthMm: this.labelWidthMm,
      labelHeightMm: this.labelHeightMm,
      currencySymbol: this.currencySymbol,
    };

    try {
      const existingIndex = this.savedLayouts.findIndex(layout => layout.name === layoutName);

      if (existingIndex >= 0) {
        this.savedLayouts[existingIndex].data = layoutData;
        this.savedLayouts[existingIndex].timestamp = Date.now();
      } else {
        this.savedLayouts.push({
          name: layoutName,
          data: layoutData,
          timestamp: Date.now(),
        });
      }

      this.currentLayoutName = layoutName;
      const singleLayout: { name: string, data: ILabelDesign, timestamp: number }[] = [];
      singleLayout.push({
        name: layoutName,
        data: layoutData,
        timestamp: Date.now(),
      });
      await this.collectionStoresSettingsService.setDocument(
        path_stores_storeId_settings_shelf_talkers_settings(this.selectedStore.storeId),
        {
          layouts: this.fromStore ? this.savedLayouts : singleLayout,
        },
      );
      if (!this.fromStore) {
        this.savedLayouts = [];
        this.savedLayouts.push({
          name: layoutName,
          data: layoutData,
          timestamp: Date.now(),
        });
        this.fromStore = true;
      }
      this.showLayoutDialog = false;

      this.hasUnsavedChanges = false;

      if (this.pendingStoreChange) {
        const storeToChangeTo = this.pendingStoreChange;
        this.pendingStoreChange = null;
        this.changeToStore(storeToChangeTo);
      }

      const successProps: ISharedModalBasic = {
        buttonAccept: false,
        buttonAcceptText: '',
        buttonClose: true,
        buttonCloseCross: false,
        buttonCloseText: 'Close',
        modalTitle: 'Layout Saved',
        contextHeading: `Layout "${layoutName}" saved successfully`,
        modalTitleIcon: IconsEnum.infoSlabCircleOutline,
        modalTitleIconColor: this.iconColourEnum.green,
        contextDescription: [`This layout will now be available in shelf talkers`],
      };

      const successModal = await this.modalController.create({
        component: SharedModalBasicComponent,
        componentProps: successProps,
        cssClass: ['shared-basic-modal-css-small'],
      });
      await successModal.present();
    } catch (error) {
      console.error('Error saving layout:', error);

      const errorProps: ISharedModalBasic = {
        buttonAccept: false,
        buttonAcceptText: '',
        buttonClose: true,
        buttonCloseCross: false,
        buttonCloseText: 'Close',
        modalTitle: 'Error',
        modalTitleIcon: IconsEnum.closeCircleOutline,
        modalTitleIconColor: this.iconColourEnum.red,
        contextDescription: ['Failed to save layout. Please try again.'],
      };

      const errorModal = await this.modalController.create({
        component: SharedModalBasicComponent,
        componentProps: errorProps,
        cssClass: ['shared-basic-modal-css-small'],
      });
      await errorModal.present();
    }
  }

  async handleLoadLayout(layoutName: string): Promise<void> {
    try {
      const layout = await this.collectionStoresSettingsService.getDocument(
        path_stores_storeId_settings_shelf_talkers_settings(this.selectedStore.storeId),
      );

      const selectedLayout = layout['layouts'].find(layout => layout.name === layoutName);
      if (!selectedLayout) {
        const errorProps: ISharedModalBasic = {
          buttonAccept: false,
          buttonAcceptText: '',
          buttonClose: true,
          buttonCloseCross: false,
          buttonCloseText: 'Close',
          modalTitle: 'Error',
          modalTitleIcon: IconsEnum.closeCircleOutline,
          modalTitleIconColor: this.iconColourEnum.red,
          contextDescription: ['Selected layout not found.'],
        };

        const errorModal = await this.modalController.create({
          component: SharedModalBasicComponent,
          componentProps: errorProps,
          cssClass: ['shared-basic-modal-css-small'],
        });
        await errorModal.present();
        return;
      }

      const layoutData = selectedLayout.data;

      this.labelWidthMm = layoutData.labelWidthMm;
      this.labelHeightMm = layoutData.labelHeightMm;
      this.labelItems = layoutData.labelItems;
      this.currentLayoutName = layoutName;

      await this.collectionStoresSettingsService.setDocument(
        path_stores_storeId_settings_shelf_talkers_settings(this.selectedStore.storeId),
        {
          layouts: layout['layouts'],
        },
      );

      this.showLayoutDialog = false;

      this.hasUnsavedChanges = false;

      if (this.pendingStoreChange) {
        const storeToChangeTo = this.pendingStoreChange;
        this.pendingStoreChange = null;
        this.changeToStore(storeToChangeTo);
      }

      void Promise.resolve().then(() => {
        this.updateLabelSize();
      });

    } catch (error) {
      console.error('Error loading layout:', error);

      const errorProps: ISharedModalBasic = {
        buttonAccept: false,
        buttonAcceptText: '',
        buttonClose: true,
        buttonCloseCross: false,
        buttonCloseText: 'Close',
        modalTitle: 'Error',
        modalTitleIcon: IconsEnum.closeCircleOutline,
        modalTitleIconColor: this.iconColourEnum.red,
        contextDescription: ['Failed to load layout. Please try again.'],
      };

      const errorModal = await this.modalController.create({
        component: SharedModalBasicComponent,
        componentProps: errorProps,
        cssClass: ['shared-basic-modal-css-small'],
      });
      await errorModal.present();
    }
  }

  async handleDeleteLayout(data: { name: string, event: Event }): Promise<void> {
    data.event.stopPropagation();

    const componentProps: ISharedModalBasic = {
      buttonAccept: true,
      buttonAcceptText: 'Delete',
      buttonClose: true,
      buttonCloseCross: false,
      buttonCloseText: 'Cancel',
      modalTitle: 'Delete Layout',
      modalTitleIcon: IconsEnum.deleteOutline,
      modalTitleIconColor: this.iconColourEnum.red,
      contextHeading: 'This action cannot be undone',
      contextDescription: [
        `Are you sure you want to delete the layout "${data.name}"?`,
        '',
        'This action will permanently remove this layout from the system.',
      ],
    };

    const modalController = await this.modalController.create({
      backdropDismiss: false,
      component: SharedModalBasicComponent,
      componentProps,
      cssClass: ['shared-basic-modal-css-small'],
    });
    await modalController.present();

    const modalResponse: IModalDismissResult<ISharedModalBasicResponse> = await modalController.onDidDismiss();

    if (modalResponse?.data?.buttonPressed === 'button-accept') {
      const index = this.savedLayouts.findIndex(layout => layout.name === data.name);
      if (index !== -1) {
        this.savedLayouts.splice(index, 1);

        let newSelectedLayout = this.currentLayoutName;
        if (this.currentLayoutName === data.name) {
          newSelectedLayout = this.savedLayouts.length > 0 ? this.savedLayouts[0].name : 'Default Layout';
          this.currentLayoutName = newSelectedLayout;

          if (newSelectedLayout === 'Default Layout') {
            this.resetLabelDesigner();
          }
        }

        await this.collectionStoresSettingsService.setDocument(
          path_stores_storeId_settings_shelf_talkers_settings(this.selectedStore.storeId),
          {
            layouts: this.savedLayouts,
          },
        );

        const successProps: ISharedModalBasic = {
          buttonAccept: false,
          buttonAcceptText: '',
          buttonClose: true,
          buttonCloseCross: false,
          buttonCloseText: 'Close',
          modalTitle: 'Success',
          modalTitleIcon: IconsEnum.infoSlabCircleOutline,
          modalTitleIconColor: this.iconColourEnum.green,
          contextDescription: [`Layout "${data.name}" deleted successfully`],
        };

        const successModal = await this.modalController.create({
          component: SharedModalBasicComponent,
          componentProps: successProps,
          cssClass: ['shared-basic-modal-css-small'],
        });
        await successModal.present();
      }
    }
  }

  //endregion

  closeLayoutDialog(): void {
    this.showLayoutDialog = false;
  }

  //#region testing and printing
  async generateRandomTestData(): Promise<void> {
    const testItems: ITestLabelData[] = [];
    const hasLogoInDesign = this.labelItems.some(item => item.isLogo && item.type === 'image');
    const isValid = await isValidBase64(this.storeData);
    const logoUrl = isValid ? this.storeData : '/assets/logos/Build-It-Logo-RED.png';

    const descriptionLengths = [10, 30, 50];

    for (let i = 0; i < descriptionLengths.length; i++) {
      const length = descriptionLengths[i];

      testItems.push({
        desc: this.generateRandomDescription(length, length),
        code: `ITEM-${Math.floor(Math.random() * 10000).toString().padStart(4, '0')}-${i + 1}`,
        binL: `${String.fromCharCode(65 + Math.floor(Math.random() * 26))}${Math.floor(Math.random() * 10)}-${String.fromCharCode(65 + Math.floor(Math.random() * 26))}${Math.floor(Math.random() * 10)}`,
        price: Math.floor(Math.random() * 99999) / 100,
        barcode: this.generateRandomBarcode(),
        date: new Date(),
        suppCode: `${Math.floor(Math.random() * 10000).toString()}`,
        logoUrl: logoUrl,
        companyName: this.companyName
      });
    }

    this.printLabels(testItems);
  }

  async testSingleLabelGeneration(): Promise<void> {
    const testItems: ITestLabelData[] = [];
    const isValid = await isValidBase64(this.storeData);
    const logoUrl = isValid ? this.storeData : '/assets/logos/Build-It-Logo-RED.png';

    testItems.push({
      desc: this.generateRandomDescription(10, 50),
      code: `ITEM-${Math.floor(Math.random() * 10000).toString().padStart(4, '0')}-1`,
      binL: `${String.fromCharCode(65 + Math.floor(Math.random() * 26))}${Math.floor(Math.random() * 10)}-${String.fromCharCode(65 + Math.floor(Math.random() * 26))}${Math.floor(Math.random() * 10)}`,
      price: Math.floor(Math.random() * 99999) / 100,
      barcode: this.generateRandomBarcode(),
      date: new Date(),
      logoUrl: logoUrl,
      companyName: this.companyName
    });
    this.printLabels(testItems);
  }

//endregion

  printLabels(testItems?: ITestLabelData[]): void {
    const designItemsCopy = JSON.parse(JSON.stringify(this.labelItems));

    const originalItems = this.labelItems;
    this.labelItems = designItemsCopy;

    const design: ILabelDesign = {
      labelItems: [...this.labelItems],
      labelWidthMm: this.labelWidthMm,
      labelHeightMm: this.labelHeightMm,
      currencySymbol: this.currencySymbol,
    };

    this.labelItems = originalItems;

    const data = testItems || this.previewData.map(preview => ({
      code: preview.itemCode,
      desc: preview.description,
      binL: preview.binLoc,
      price: parseFloat(preview.price.replace(/[^0-9.]/g, '')),
      barcode: preview.barcode,
      date: new Date(),
      logoUrl: preview.logoUrl,
      companyName: preview.companyName

    }));

    this.printRequest.emit({design, data});
  }

  //#region utility functions
  async generateBarcodeImage(barcode: string): Promise<string> {

    this.barcodeCache.delete(barcode);

    const tempCanvas = document.createElement('canvas');
    tempCanvas.height = 200;
    tempCanvas.width = 300;

    try {
      JsBarcode(tempCanvas, barcode, {
        width: 2,
        height: 150,
        displayValue: false,
        margin: 0,
        textMargin: 0,
        font: '0px',
      });

      const finalCanvas = document.createElement('canvas');
      const ctx = tempCanvas.getContext('2d');

      const imageData = ctx.getImageData(0, 0, tempCanvas.width, tempCanvas.height);
      const data = imageData.data;

      let barcodeBottom = 0;
      for (let y = 0; y < tempCanvas.height * 0.8; y++) {
        let hasBarcode = false;
        for (let x = 0; x < tempCanvas.width; x++) {
          const idx = (y * tempCanvas.width + x) * 4;
          if (data[idx] < 250 || data[idx + 1] < 250 || data[idx + 2] < 250) {
            hasBarcode = true;
            break;
          }
        }
        if (hasBarcode) {
          barcodeBottom = Math.max(barcodeBottom, y);
        }
      }

      barcodeBottom += 5;

      finalCanvas.width = tempCanvas.width;
      finalCanvas.height = barcodeBottom;

      const finalCtx = finalCanvas.getContext('2d');
      finalCtx.fillStyle = 'white';
      finalCtx.fillRect(0, 0, finalCanvas.width, finalCanvas.height);
      finalCtx.drawImage(
        tempCanvas,
        0, 0, tempCanvas.width, barcodeBottom,
        0, 0, finalCanvas.width, finalCanvas.height,
      );

      const dataUrl = finalCanvas.toDataURL('image/png');

      this.barcodeCache.set(barcode, dataUrl);

      return dataUrl;
    } catch (error) {
      console.error('Error generating barcode:', error);
      throw error;
    }
  }


  /**
   * Sets the zoom level to a specific value and updates the canvas
   * @param level The new zoom level
   */
  setZoomLevel(level: number): void {
    if (this.zoomLevel === level) return;

    this.zoomLevel = level;

    if (this.canvasRef) {
      this.canvasRef.nativeElement.width = this.zoomedLabelWidth;
      this.canvasRef.nativeElement.height = this.zoomedLabelHeight;

      this.drawGrid();
      void this.drawItems();
    }
  }

  /**
   * Increases the zoom level to the next available level
   */
  zoomIn(): void {
    const currentIndex = this.zoomLevels.indexOf(this.zoomLevel);
    if (currentIndex < this.zoomLevels.length - 1) {
      this.setZoomLevel(this.zoomLevels[currentIndex + 1]);
    }
  }

  /**
   * Decreases the zoom level to the previous available level
   */
  zoomOut(): void {
    const currentIndex = this.zoomLevels.indexOf(this.zoomLevel);
    if (currentIndex > 0) {
      this.setZoomLevel(this.zoomLevels[currentIndex - 1]);
    }
  }

  /**
   * Resets the zoom level to 100%
   */
  resetZoom(): void {
    this.setZoomLevel(1);
  }


  private handleWindowResize(): void {
    clearTimeout(this.resizeTimeout);
    this.resizeTimeout = setTimeout(() => {
      this.calculatePreviewScale();
    }, 250);
  }

  private calculatePreviewScale(): void {
    const container = document.querySelector('.preview-labels');
    if (!container) return;

    const containerWidth = container.clientWidth;

    if (this.labelWidth <= 0 || this.labelHeight <= 0) {
      this.previewScale = 1;
      return;
    }

    const labelsPerRow = Math.max(1, Math.min(4, Math.floor(containerWidth / 300)));
    const gapSpace = (labelsPerRow - 1) * 20;
    const availableWidth = containerWidth - gapSpace;

    let scale = Math.min(1, availableWidth / (this.labelWidth * labelsPerRow));

    scale = Math.max(0.2, scale);

    if (this.labelWidth < 100 || this.labelHeight < 50) {
      scale = Math.min(scale, 2);
    }

    this.previewScale = scale;

  }

  private drawResizeHandles(item: ILabelVariable): void {
    const handleSize = 8;
    const halfHandle = handleSize / 2;

    this.ctx.fillStyle = '#1976d2';

    this.ctx.fillRect(item.x - halfHandle, item.y - halfHandle, handleSize, handleSize);

    this.ctx.fillRect(item.x + item.width - halfHandle, item.y - halfHandle, handleSize, handleSize);

    this.ctx.fillRect(item.x - halfHandle, item.y + item.height - halfHandle, handleSize, handleSize);

    this.ctx.fillRect(item.x + item.width - halfHandle, item.y + item.height - halfHandle, handleSize, handleSize);
  }

  private getResizeHandle(x: number, y: number): 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | null {
    if (!this.selectedItem) return null;

    const item = this.selectedItem;
    const handleSize = 8;
    const halfHandle = handleSize / 2;

    if (Math.abs(x - item.x) <= halfHandle && Math.abs(y - item.y) <= halfHandle) {
      return 'top-left';
    }

    if (Math.abs(x - (item.x + item.width)) <= halfHandle && Math.abs(y - item.y) <= halfHandle) {
      return 'top-right';
    }

    if (Math.abs(x - item.x) <= halfHandle && Math.abs(y - (item.y + item.height)) <= halfHandle) {
      return 'bottom-left';
    }

    if (Math.abs(x - (item.x + item.width)) <= halfHandle && Math.abs(y - (item.y + item.height)) <= halfHandle) {
      return 'bottom-right';
    }

    return null;
  }

  private getItemAtPosition(x: number, y: number): ILabelVariable | null {
    for (let i = this.labelItems.length - 1; i >= 0; i--) {
      const item = this.labelItems[i];
      if (x >= item.x && x <= item.x + item.width &&
        y >= item.y && y <= item.y + item.height) {
        return item;
      }
    }
    return null;
  }

  private drawLogoPlaceholder(ctx: CanvasRenderingContext2D, item: ILabelVariable): void {
    ctx.fillStyle = '#f0f0f0';
    ctx.fillRect(item.x + 1, item.y + 1, item.width - 2, item.height - 2);
    ctx.fillStyle = 'black';
    ctx.font = '12px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    let text = 'Image';

    if (item.value === '{{logo}}' || item.isLogo === true) {
      text = 'Logo';
    } else if (item.value === '{{barcodeGraphic}}') {
      text = 'Barcode Image';
    }

    ctx.fillText(text, item.x + item.width / 2, item.y + item.height / 2);
  }

  //endregion

  private generateRandomDescription(minLength: number, maxLength: number): string {
    const words = [
      'Product', 'Item', 'Goods', 'Merchandise', 'Stock', 'Supply', 'Inventory',
      'Quality', 'Premium', 'Standard', 'Basic', 'Deluxe', 'Economy', 'Value',
      'Special', 'Original', 'Genuine', 'Authentic', 'New', 'Fresh', 'Latest',
      'Small', 'Large', 'Medium', 'Mini', 'Jumbo', 'Compact', 'Portable',
      this.iconColourEnum.red, 'Blue', this.iconColourEnum.green, this.iconColourEnum.yellow, 'Black', 'White', 'Purple', 'Orange',
      'Metal', 'Plastic', 'Wooden', 'Glass', 'Ceramic', 'Cotton', 'Leather',
      'Professional', 'Industrial', 'Commercial', 'Residential', 'Imported',
      'Handmade', 'Recyclable', 'Reusable', 'Disposable', 'Ergonomic',
      'Modern', 'Classic', 'Vintage', 'Contemporary', 'Traditional',
      'Luxury', 'Budget', 'Essential', 'Advanced', 'High-End',
    ];

    const adjectives = [
      'Amazing', 'Excellent', 'Superior', 'High-Quality', 'Durable',
      'Lightweight', 'Heavy-Duty', 'Portable', 'Affordable', 'Premium',
      'Elegant', 'Stylish', 'Practical', 'Versatile', 'Innovative',
    ];

    if (maxLength === 30) {
      while (true) {
        const adj1 = adjectives[Math.floor(Math.random() * adjectives.length)];
        const adj2 = adjectives[Math.floor(Math.random() * adjectives.length)];
        const word = words[Math.floor(Math.random() * words.length)];
        const desc = `${adj1} ${word} ${adj2}`.trim();

        if (desc.length < 30) {
          return desc + ' ' + 'item'.repeat(Math.ceil((30 - desc.length - 1) / 4)).slice(0, 30 - desc.length - 1);
        } else if (desc.length > 30) {
        } else {
          return desc;
        }
      }
    }

    if (maxLength === 50) {
      while (true) {
        const adj1 = adjectives[Math.floor(Math.random() * adjectives.length)];
        const adj2 = adjectives[Math.floor(Math.random() * adjectives.length)];
        const word1 = words[Math.floor(Math.random() * words.length)];
        const word2 = words[Math.floor(Math.random() * words.length)];
        const desc = `${adj1} ${word1} ${adj2} ${word2} product description`.trim();

        if (desc.length < 50) {
          return desc + ' ' + 'item'.repeat(Math.ceil((50 - desc.length - 1) / 4)).slice(0, 50 - desc.length - 1);
        } else if (desc.length > 50) {
        } else {
          return desc;
        }
      }
    }

    let description = '';
    if (minLength > 20) {
      description = adjectives[Math.floor(Math.random() * adjectives.length)];
    }

    while (description.length < minLength) {
      const randomWord = words[Math.floor(Math.random() * words.length)];
      if (description.length + randomWord.length + 1 <= maxLength) {
        description += (description ? ' ' : '') + randomWord;
      } else {
        if (description.length >= minLength * 0.9) {
          break;
        }

        for (let i = 0; i < 5; i++) {
          const shortWord = words[Math.floor(Math.random() * words.length)];
          if (description.length + shortWord.length + 1 <= maxLength) {
            description += (description ? ' ' : '') + shortWord;
            break;
          }
        }
        if (description.length < minLength * 0.9) {
          break;
        }
      }
    }

    return description;
  }

  private generateRandomBarcode(): string {
    return Array.from({length: 13}, () => Math.floor(Math.random() * 10)).join('');
  }

  private preloadImage(src: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';

      if (!src || src === 'undefined' || src === 'null') {
        img.src = this.defaultLogoData;
        img.onload = (): void => {
          resolve(img);
        };
        return;
      }
      let imageSrc = src;
      if (src.startsWith('/assets')) {
        imageSrc = window.location.origin + src;
      }

      img.onload = (): void => {
        resolve(img);
      };

      img.onerror = (err): void => {
        console.warn(`Failed to load image from ${imageSrc}, using default logo`, err);
        img.src = ` /assets/logos/Build-It-Logo-RED.png`;
      };

      img.src = imageSrc;
    });
  }

  private updatePreviewText(
    ctx: CanvasRenderingContext2D,
    item: ILabelVariable,
    value: string,
    textX: number,
    textY: number,
    fontSize: number,
    maxWidth: number): void {
    if (!value) return;

    if (item.overflow === 'wrap') {
      const words = value.split(' ');
      const lineHeight = fontSize * 1.2;
      const maxLines = Math.floor((item.height - 8) / lineHeight);
      const lines: string[] = [];
      let currentLine = '';

      for (const word of words) {
        const testLine = currentLine + (currentLine ? ' ' : '') + word;
        const metrics = ctx.measureText(testLine);
        const testWidth = metrics.width;

        if (testWidth > maxWidth && currentLine !== '') {
          lines.push(currentLine);
          currentLine = word;
        } else {
          currentLine = testLine;
        }
      }
      if (currentLine) {
        lines.push(currentLine);
      }

      const linesToDraw = lines.slice(0, maxLines);
      let y = textY;

      if (item.verticalAlign === 'middle') {
        y = item.y + (item.height / 2) - ((linesToDraw.length * lineHeight) / 2) + (lineHeight / 2);
      } else if (item.verticalAlign === 'bottom') {
        y = item.y + item.height - 4 - (linesToDraw.length - 1) * lineHeight;
      }

      for (const line of linesToDraw) {
        ctx.fillText(line, textX, y);
        y += lineHeight;
      }
    } else if (item.overflow === 'shrink') {
      ctx.fillText(value, textX, textY);
    } else {
      ctx.fillText(value, textX, textY);
    }
  }

  private snapToGrid(value: number, isHorizontal: boolean = true): number {
    const gridSize = this.mmToPixels;
    const snappedValue = Math.round(value / gridSize) * gridSize;

    if ((this.isDragging || this.isResizing) && this.canvasRef && this.ctx) {
      this.showSnapPoint(snappedValue, isHorizontal);
    }

    return snappedValue;
  }

  private showSnapPoint(position: number, isHorizontal: boolean = true): void {
    if (!this.canvasRef || !this.ctx) return;

    const canvas = this.canvasRef.nativeElement;
    const highlightSize = 4;
    this.ctx.save();

    this.ctx.scale(this.zoomLevel, this.zoomLevel);

    this.ctx.fillStyle = 'rgba(25, 118, 210, 0.5)';

    if (isHorizontal) {
      this.ctx.fillRect(position - highlightSize / 2, 0, highlightSize, this.labelHeight);
    } else {
      this.ctx.fillRect(0, position - highlightSize / 2, this.labelWidth, highlightSize);
    }

    this.ctx.restore();

    setTimeout(() => {
      this.drawGrid();
      void this.drawItems();
    }, 200);
  }

  private changeToStore(store: IStore): void {
    this.selectedStore = store;
    this.fromStore = true;
    if (store) {
      void this.loadSavedLayouts();
      void this.collectionStoresSettingsService.getDocument(createSharedPaths().setStoreId(this.selectedStore.storeId).storeInfo()).then(async (storeData) => {
        this.storeData = storeData['companyLogo'];
        this.companyName = storeData['companyName'] ?? '';
        const isValid = await isValidBase64(this.storeData);
        const logoUrl = isValid ? this.storeData : '/assets/logos/Build-It-Logo-RED.png';
        this.previewData.forEach((label: IPreviewData) => {
          label.logoUrl = logoUrl;
        });
      });
      this.store.dispatch(setSelectedStore({selectedStore: store}));
    }

    this.hasUnsavedChanges = false;
  }

  private async showUnsavedChangesConfirmation(newStore: IStore): Promise<void> {
    const componentProps: ISharedModalBasic = {
      buttonAccept: true,
      buttonAcceptText: 'Discard Changes',
      buttonClose: true,
      buttonCloseCross: false,
      buttonCloseText: 'Cancel',
      buttonSecondary: true,
      buttonSecondaryText: 'Save and Continue',
      modalTitle: 'Unsaved Changes',
      modalTitleIcon: IconsEnum.infoSlabCircleOutline,
      modalTitleIconColor: this.iconColourEnum.yellow,
      contextHeading: 'You have unsaved changes',
      contextDescription: [
        'If you continue to change stores, your current unsaved changes will be lost.',
        'Do you want to continue or save your changes first?',
      ],
    };

    const modal = await this.modalController.create({
      component: SharedModalBasicComponent,
      componentProps,
      cssClass: ['shared-basic-modal-css-small'],
    });

    await modal.present();

    const {data} = await modal.onDidDismiss();

    if (data?.buttonPressed === 'button-accept') {
      this.changeToStore(newStore);
    } else if (data?.buttonPressed === 'button-secondary') {
      this.store.dispatch(setSelectedStore({selectedStore: this.selectedStore}));
      this.showLayoutDialog = true;

      this.pendingStoreChange = newStore;
    } else {
      this.store.dispatch(setSelectedStore({selectedStore: this.selectedStore}));
    }
  }
}
